import { useEffect, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { analytics, AnalyticsEvent } from "../lib/analytics";
import { trackApiError } from "../lib/analytics/errorTracking";
import { BallMark } from "../App";
import { Button, ErrorNote, Icon, Input } from "../components/ui";

/**
 * First sign-in only.
 *
 * Firebase gives us an account but no unique name, and two "Dave"s on a shared leaderboard are
 * indistinguishable — so a username is required before anything else. The API suggests a free
 * one derived from their Google name or email, which most people will just accept.
 */
export function ChooseUsernamePage() {
  const { profileStatus, refresh, logout } = useAuth();
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    if (profileStatus?.suggestedDisplayName) {
      setDisplayName(profileStatus.suggestedDisplayName);
    }
  }, [profileStatus?.suggestedDisplayName]);

  const mutation = useMutation({
    mutationFn: () => api.auth.createProfile(displayName.trim()),
    onSuccess: () => {
      analytics.track(AnalyticsEvent.ProfileCreated);
      return refresh();
    },
    onError: (error) => trackApiError(error, "create_profile"),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <div className="relative grid min-h-dvh place-items-center overflow-hidden p-4 sm:p-6">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 12%, var(--t-accent-soft), transparent 46%)",
        }}
      />

      <div className="card animate-in relative w-full max-w-md p-6 sm:p-8">
        <div className="mb-8 flex items-center gap-2.5 text-lg font-bold text-fg">
          <BallMark size={30} />
          Last Man Standing
        </div>

        <p className="label mb-2 text-accent-text">One last step</p>
        <h1 className="display-title text-4xl text-fg">Pick your username</h1>
        <p className="mt-3 text-sm leading-relaxed text-fg-subtle">
          This is what everyone else sees on the leaderboard. You can change it later, and it's
          never used to sign in.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Input
            label="Username"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="lewis"
            required
            minLength={2}
            maxLength={24}
            autoFocus
            icon="user"
            pattern="[a-zA-Z0-9_ \-]+"
            hint="Letters, numbers, spaces, hyphens and underscores."
          />

          {mutation.error && (
            <ErrorNote>
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : "Couldn't save that username."}
            </ErrorNote>
          )}

          <Button
            type="submit"
            size="lg"
            block
            loading={mutation.isPending}
            disabled={displayName.trim().length < 2}
          >
            Continue
            <Icon name="arrowRight" className="size-4" />
          </Button>
        </form>

        <div className="mt-6 flex items-center justify-between gap-3 border-t border-line pt-5 text-[13px] text-fg-faint">
          {profileStatus?.email && <span className="truncate">{profileStatus.email}</span>}
          <button
            onClick={() => void logout()}
            className="shrink-0 font-medium transition-colors hover:text-fg"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
